<script setup lang="ts">
import { useMediaQuery } from "@vueuse/core";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSearchable } from "../../composables/Query/searchable";
import VIcon from "../Icon/VIcon.vue";
import VTooltip from "../Tooltip/VTooltip.vue";
import { searchControlCva, searchWrapperCva } from "./VSearch.cva";

const props = withDefaults(defineProps<{
  placeholderText?: string
  clearSearchText?: string
  hasMargin?: boolean
}>(), {
  placeholderText: "",
  clearSearchText: "",
  hasMargin: true,
});

const { t } = useI18n();
const { searchValue } = useSearchable();

const inputRef = ref<HTMLInputElement | null>(null);
const hovered = ref(false);
const focused = ref(false);
const canHover = useMediaQuery("(hover: hover)");

const hasValue = computed(() => (searchValue.value ?? "").trim() !== "");
const isExpanded = computed(() =>
  (canHover.value && hovered.value) || focused.value || hasValue.value,
);

const tooltipText = computed(() => t("base.search"));

const placeholder = computed(() =>
  props.placeholderText || t("base.table.search_placeholder"),
);

const clearLabel = computed(() =>
  props.clearSearchText || t("base.table.clear_search"),
);

const inputValue = computed({
  get: () => searchValue.value ?? "",
  set: (value: string) => {
    searchValue.value = value;
  },
});

const wrapperClasses = computed(() => searchWrapperCva({ hasMargin: props.hasMargin }));

const controlClasses = computed(() => searchControlCva({
  expanded: isExpanded.value,
  active: hasValue.value,
  expandedCursor: isExpanded.value,
}));

function focusInput() {
  inputRef.value?.focus();
}

function onClear(event: MouseEvent) {
  event.stopPropagation();
  searchValue.value = "";
  inputRef.value?.focus();
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== "Escape") {
    return;
  }
  if (hasValue.value) {
    searchValue.value = "";
    return;
  }
  inputRef.value?.blur();
}
</script>

<template>
  <span :class="wrapperClasses">
    <VTooltip
      :content="tooltipText"
      placement="top"
      theme="verbleif-tooltip-solid"
      :disabled="isExpanded"
    >
      <template #reference>
        <div
          role="search"
          data-test="search-control"
          :class="controlClasses"
          @mouseenter="hovered = true"
          @mouseleave="hovered = false"
          @click="focusInput"
        >
          <span class="absolute inset-y-0 left-0 flex items-center justify-center w-8 pointer-events-none">
            <VIcon
              class="settings transition-opacity duration-200"
              name="search"
            />
          </span>
          <input
            ref="inputRef"
            v-model="inputValue"
            type="text"
            name="search"
            autocomplete="off"
            :placeholder="placeholder"
            :aria-label="tooltipText"
            class="h-full w-full min-w-0 appearance-none border-none bg-transparent pl-8 pr-8 text-sm text-inherit placeholder:text-(--input-placeholder) focus:outline-hidden"
            @focus="focused = true"
            @blur="focused = false"
            @keydown="onKeydown"
          >
          <button
            type="button"
            data-test="search-clear"
            class="absolute inset-y-0 right-0 flex items-center justify-center w-8 text-inherit transition-[opacity,filter,scale] duration-300 ease-[cubic-bezier(0.2,0,0,1)] hover:opacity-80"
            :class="hasValue
              ? 'scale-100 opacity-100 blur-none'
              : 'pointer-events-none scale-[0.25] opacity-0 blur-[4px]'"
            :aria-label="clearLabel"
            :aria-hidden="!hasValue"
            :tabindex="hasValue ? 0 : -1"
            @mousedown.prevent
            @click="onClear"
          >
            <VIcon
              name="x"
              class="font-bold"
            />
          </button>
        </div>
      </template>
    </VTooltip>
  </span>
</template>
